// «Мой кабинет» — то, что видит сам контрибьютор (0004): его часы, качество,
// заработок по его ставке, дни и записи с причинами отказов. Никаких чужих
// данных на этом экране нет by design — бэкенд отдаёт /my/stats только по
// contributor-сессии и только про самого человека.
import { useEffect, useState } from 'preact/hooks';
import type { VistaCaptureRow, VistaMyLedgerRes } from '@weern/contracts';
import type { Lang, T } from '../i18n';
import { api, type VistaMyStatsRes } from '../api';
import { PlayerModal } from '../components/PlayerModal';

/** A 'rejected' clip with bytes=0 was discarded before any footage landed
 *  (a cancelled recording, or an upload R2 expired before it finished) —
 *  there is nothing in R2 to play. 'recording' is still mid-upload — same
 *  story, no file yet. `bytes` is optional on the shared contract row (only
 *  /my/stats fills it in); treat "not sent" the same as 0. */
const hasPlayableFile = (r: Pick<VistaCaptureRow, 'status' | 'bytes'>): boolean =>
  r.status !== 'recording' && !(r.status === 'rejected' && (r.bytes ?? 0) === 0);

/**
 * Часы для человека, который смотрит на СВОЙ труд. Двадцать минут — это 0.33
 * часа, и «0.3 ч» читается как «почти ничего», а «0.0 ч» после двухминутной
 * записи выглядит так, будто её не засчитали. Поэтому меньше часа показываем
 * минутами: цифра должна совпадать с ощущением от смены.
 */
const fmtH = (h: number, t: T): string =>
  h > 0 && h < 1 ? `${Math.round(h * 60)} ${t('mine.min')}` : `${h.toFixed(h >= 100 ? 0 : 1)} ${t('mine.hours')}`;
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
const fmtDur = (sec: number | null): string => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtDateTime = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Wording aimed at the person who filmed it: they care whether the clip is
// still moving through our hands or already judged by the client. «Ждём
// вердикт» is honest about the wait — the client answers by hand, not on a timer.



/** «За что начислено» — контрибьютору видны его собственные движения денег.
 *  Свернуто по умолчанию: это справка, а не главный экран. */
function LedgerPanel({ ledger, t }: { ledger: VistaMyLedgerRes | null | 'error'; t: T }) {
  const [open, setOpen] = useState(false);
  if (ledger === 'error' || !ledger || ledger.rows.length === 0) return null;
  return (
    <div class="panel">
      <button type="button" class="link" onClick={() => setOpen((v) => !v)}>
        {t('mine.ledger.title')} {open ? '▴' : '▾'}
      </button>
      {open && (
        <div class="tablewrap responsive-table" style={{ marginTop: '10px' }}>
          <table>
            <tbody>
              {ledger.rows.map((r) => (
                <tr key={r.id}>
                  <td data-label={t('mine.col.when')}>{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td data-label={t('mine.ledger.what')}>{r.note ?? r.taskSlug ?? r.type}</td>
                  <td data-label={t('mine.ledger.amount')}>
                    {(Math.abs(r.amountCents) / 100).toFixed(2)} $
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MySpacePage({ t }: { t: T; lang: Lang }) {
  const [data, setData] = useState<VistaMyStatsRes | null | 'error'>(null);
  const [tab, setTab] = useState<'all' | 'rejected'>('all');
  const [ledger, setLedger] = useState<VistaMyLedgerRes | null | 'error'>(null);
  const [playFor, setPlayFor] = useState<VistaCaptureRow | null>(null);

  useEffect(() => {
    api.myStats().then(setData).catch(() => setData('error'));
    // Отдельный запрос, отдельная ошибка: детализация начислений — это
    // дополнение к KPI-плиткам, а не их условие. Плитки не должны погаснуть
    // только потому, что список движений не загрузился.
    api.myLedger().then(setLedger).catch(() => setLedger('error'));
  }, []);

  if (data === 'error') return <div class="empty">{t('err.stats')}</div>;
  if (!data) return <div class="empty">{t('loading')}</div>;

  const m = data.me;
  const rejected = data.recent.filter((r) => r.status === 'rejected');
  const list = tab === 'rejected' ? rejected : data.recent;
  const days = [...data.daily].reverse();
  const maxDay = Math.max(1, ...days.map((d) => d.filmedHours));

  return (
    <>
      <div class="card-head">
        <div>
          <h2>{m.name ?? m.email}</h2>
          <div class="muted">{m.name ? m.email : t('mine.title')}</div>
        </div>
      </div>

      <div class="tiles">
        <div class="tile"><div class="n">{fmtH(m.acceptedHours, t)}</div><div class="t">{t('mine.accepted')}</div></div>
        <div class="tile"><div class="n">{fmtH(m.pendingHours, t)}</div><div class="t">{t('mine.pending')}</div></div>
        <div class="tile"><div class="n">{fmtH(m.rejectedHours, t)}</div><div class="t">{t('mine.rejected')}</div></div>
        <div class="tile"><div class="n">{fmtH(m.filmedHours, t)}</div><div class="t">{t('mine.filmed')}</div></div>
        <div class="tile">
          <div class="n">{fmtMoney(m.earnedCents)}</div>
          <div class="t">
            {m.rateCents == null
              ? t('mine.norate')
              : `${t('mine.earned')} · ${t('mine.perhour', { rate: `$${(m.rateCents / 100).toFixed(2)}` })}`}
          </div>
        </div>
      </div>

      {m.pendingHours > 0 && (
        <div class="strip warn">
          {t('mine.pendingNote', { hours: fmtH(m.pendingHours, t) })}
        </div>
      )}

      <LedgerPanel ledger={ledger} t={t} />

      {days.length > 0 && (
        <div class="panel">
          <h3>{t('mine.byDay')}</h3>
          <div class="bars">
            {days.map((d) => (
              <div class="bar-col" key={d.day} title={`${d.day}: ${fmtH(d.filmedHours, t)}`}>
                <div class="bar-stack" style={{ height: `${(d.filmedHours / maxDay) * 100}%` }}>
                  <div class="bar-rej" style={{ flex: d.rejectedHours }} />
                  <div class="bar-pend" style={{ flex: d.pendingHours }} />
                  <div class="bar-acc" style={{ flex: d.acceptedHours }} />
                </div>
                <div class="bar-label">{d.day.slice(8)}</div>
              </div>
            ))}
          </div>
          <div class="legend">
            <span><i class="sw acc" /> {t('mine.accepted')}</span>
            <span><i class="sw pend" /> {t('mine.pending')}</span>
            <span><i class="sw rej" /> {t('mine.rejected')}</span>
          </div>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>{t('mine.title')}</h3>
          <div class="seg">
            <button class={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
              {t('mine.all')} ({data.recent.length})
            </button>
            <button class={tab === 'rejected' ? 'on' : ''} onClick={() => setTab('rejected')}>
              {t('mine.rejectedTab')} ({rejected.length})
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div class="empty">
            {tab === 'rejected' ? t('mine.noRejected') : t('mine.noRecords')}
          </div>
        ) : (
          <div class="tablewrap responsive-table">
            <table>
              <thead>
                <tr>
                  <th>{t('mine.col.file')}</th>
                  <th>{t('mine.col.when')}</th>
                  <th>{t('mine.col.length')}</th>
                  <th>{t('mine.col.status')}</th>
                  <th>{t('mine.col.topic')}</th>
                  <th>{t('mine.col.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    {/* У записи рекордером имени файла нет по определению —
                        имя есть только у загруженного готового видео. Вместо
                        «no name» показываем синтетическое имя из темы и id:
                        человеку нужно ЧТО-ТО, чем клип называется в разговоре
                        («haircut-eed4») — «без имени» таким быть не может. */}
                    <td class="card-title" data-label={t('mine.col.file')}>
                      {r.fileName ?? `${r.taskSlug ?? 'clip'}-${r.id.slice(0, 4)}.mp4`}
                      {hasPlayableFile(r) && (
                        <button type="button" class="link" style={{ marginLeft: '8px' }} onClick={() => setPlayFor(r)}>
                          ▶ {t('mine.play')}
                        </button>
                      )}
                    </td>
                    <td data-label={t('mine.col.when')}>{fmtDateTime(r.recordedAt ?? r.uploadedAt)}</td>
                    <td data-label={t('mine.col.length')}>{fmtDur(r.durationSeconds)}</td>
                    <td data-label={t('mine.col.status')}>
                      <span class={`status-chip ${r.status === 'accepted' ? 'active' : r.status === 'rejected' ? 'fraud' : 'pend'}`}>
                        {t(`status.${r.status}`)}
                      </span>
                    </td>
                    <td data-label={t('mine.col.topic')}>{r.taskSlug ?? <span class="muted">—</span>}</td>
                    <td class="reason" data-label={t('mine.col.reason')}>{r.rejectReason ?? <span class="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {playFor && (
        <PlayerModal
          captureId={playFor.id}
          title={playFor.fileName ?? `${playFor.taskSlug ?? 'clip'}-${playFor.id.slice(0, 4)}.mp4`}
          srcRaw={api.captureFileUrl(playFor.id, 'raw')}
          closeLabel={t('mine.player.close')}
          preparingLabel={t('player.preparing')}
          onClose={() => setPlayFor(null)}
        />
      )}
    </>
  );
}
